import * as React from 'react'
import { cva } from 'class-variance-authority'

import { getRandomColor } from '@campai/lib-common/utils/misc'

import { Slot } from '@/components/new/lib/slot'
import { cn } from '@/components/new/lib/utils'
import type { Color } from '@/components/new/ui/colors'

type HexColor = `#${string}`
type BadgeColor = Color | HexColor

type BaseBadgeProps = React.ComponentPropsWithoutRef<'span'> & {
    asChild?: boolean
    size?: 'default' | 'lg'
}
type BadgeProps =
    | (BaseBadgeProps & {
          variant?: 'default' | 'solid'
          color: BadgeColor
          seed?: never
      })
    | (BaseBadgeProps & {
          variant: 'outline'
          color?: BadgeColor
          seed?: never
      })
    | (BaseBadgeProps & {
          variant: 'colored'
          color?: never
          seed: string
      })

const hexColorRegex = /^#(?:[A-Fa-f0-9]{3}|[A-Fa-f0-9]{6})$/

const isHexColor = (value: string): value is HexColor => {
    return hexColorRegex.test(value)
}

const getHueFromHexColor = (hexColor: HexColor) => {
    const normalizedHex =
        hexColor.length === 4 ? hexColor.replace(/./g, (char, index) => (index === 0 ? char : char + char)) : hexColor
    const r = Number.parseInt(normalizedHex.slice(1, 3), 16) / 255
    const g = Number.parseInt(normalizedHex.slice(3, 5), 16) / 255
    const b = Number.parseInt(normalizedHex.slice(5, 7), 16) / 255
    const max = Math.max(r, g, b)
    const min = Math.min(r, g, b)
    const delta = max - min

    if (delta === 0) {
        return 0
    }

    if (max === r) {
        return ((g - b) / delta + (g < b ? 6 : 0)) * 60
    }
    if (max === g) {
        return ((b - r) / delta + 2) * 60
    }

    return ((r - g) / delta + 4) * 60
}

const badgeVariants = cva(
    'group/badge inline-flex h-5 w-fit shrink-0 items-center justify-center gap-1 overflow-hidden rounded-4xl border border-transparent px-2 py-0.5 text-xs font-medium whitespace-nowrap transition-all focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5 aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 [&>svg]:pointer-events-none [&>svg]:size-3!',
    {
        variants: {
            variant: {
                default:
                    'border-[var(--badge-border-light)] bg-[var(--badge-bg-light)] text-[var(--badge-text-light)] dark:border-[var(--badge-border-dark)] dark:bg-[var(--badge-bg-dark)] dark:text-[var(--badge-text-dark)]',
                colored:
                    'border-[var(--badge-border-light)] bg-[var(--badge-bg-light)] text-[var(--badge-text-light)] dark:border-[var(--badge-border-dark)] dark:bg-[var(--badge-bg-dark)] dark:text-[var(--badge-text-dark)]',
                solid:
                    'border-[var(--badge-solid-light)] bg-[var(--badge-solid-light)] text-white dark:border-[var(--badge-solid-dark)] dark:bg-[var(--badge-solid-dark)]',
                outline: 'border-border bg-background text-foreground',
            },
            size: {
                default: '',
                lg: 'h-6 px-2.5 py-[3px] text-[13px]',
            },
        },
        defaultVariants: {
            variant: 'default',
            size: 'default',
        },
    }
)

const Badge = React.forwardRef<HTMLSpanElement, BadgeProps>(
    (
        { className, color = 'neutral', seed, style, variant = 'default', size = 'default', asChild = false, ...props },
        ref
    ) => {
        const Comp = asChild ? Slot.Root : 'span'
        const hasHexColor = isHexColor(color)
        const defaultColorStyle = React.useMemo(
            () =>
                hasHexColor
                    ? ({
                          '--badge-bg-light': `oklch(0.94 0.037 ${getHueFromHexColor(color)})`,
                          '--badge-bg-dark': `oklch(0.28 0.06 ${getHueFromHexColor(color)})`,
                          '--badge-border-light': `oklch(0.83 0.06 ${getHueFromHexColor(color)})`,
                          '--badge-border-dark': `oklch(0.5 0.09 ${getHueFromHexColor(color)})`,
                          '--badge-text-light': `oklch(0.38 0.1 ${getHueFromHexColor(color)})`,
                          '--badge-text-dark': `oklch(0.82 0.08 ${getHueFromHexColor(color)})`,
                          '--badge-solid-light': color,
                          '--badge-solid-dark': color,
                      } as React.CSSProperties)
                    : ({
                          '--badge-bg-light': `var(--color-${color}-50)`,
                          '--badge-bg-dark': `var(--color-${color}-950)`,
                          '--badge-text-light': `var(--color-${color}-700)`,
                          '--badge-text-dark': `var(--color-${color}-300)`,
                          '--badge-border-light': `var(--color-${color}-300)`,
                          '--badge-border-dark': `var(--color-${color}-700)`,
                          '--badge-solid-light': `var(--color-${color}-500)`,
                          '--badge-solid-dark': `var(--color-${color}-600)`,
                      } as React.CSSProperties),
            [color, hasHexColor]
        )
        const seededColorStyle = React.useMemo(() => {
            const color = getRandomColor(seed ?? '')
            return {
                '--badge-bg-light': color.bgLight,
                '--badge-bg-dark': color.bgDark,
                '--badge-border-light': color.borderLight,
                '--badge-border-dark': color.borderDark,
                '--badge-text-light': color.textLight,
                '--badge-text-dark': color.textDark,
            } as React.CSSProperties
        }, [seed])
        const mergedStyle = React.useMemo(
            () =>
                variant === 'default' || variant === 'solid'
                    ? { ...defaultColorStyle, ...style }
                    : variant === 'colored'
                      ? { ...seededColorStyle, ...style }
                      : style,
            [defaultColorStyle, seededColorStyle, style, variant]
        )

        return (
            <Comp
                ref={ref}
                data-slot="badge"
                data-variant={variant}
                className={cn(badgeVariants({ variant, size }), className)}
                style={mergedStyle}
                {...props}
            />
        )
    }
)

Badge.displayName = 'Badge'

export { Badge, badgeVariants }
